import { logoutAdmin, startImpersonation } from "@app/lib/admin-session";
import { environment } from "@app/relay/relay-environment";
import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import type { adminDashboard_WaitlistQuery } from "@app/queries/adminDashboard_WaitlistQuery.graphql";
import type { adminDashboard_ActiveUsersQuery } from "@app/queries/adminDashboard_ActiveUsersQuery.graphql";
import type { adminDashboard_GrantMutation } from "@app/queries/adminDashboard_GrantMutation.graphql";
import type { adminDashboard_ImpersonateMutation } from "@app/queries/adminDashboard_ImpersonateMutation.graphql";
import type { adminDashboard_SlugSetMutation } from "@app/queries/adminDashboard_SlugSetMutation.graphql";

const waitlistQuery = graphql`
  query adminDashboard_WaitlistQuery {
    adminWaitlist {
      id
      email
      name
      referralCode
      referredByEmail
      createdAt
      status
      fitScore
      referralBonus
      completenessScore
      scoreBreakdown {
        name
        points
        max
      }
      files {
        id
        filename
        byteSize
        txnCount
        detectedIncome
        parseState
      }
      attribution {
        page
        utmSource
        utmMedium
        utmCampaign
        utmContent
        landing
        ref
        referrer
      }
    }
  }
`;

const activeUsersQuery = graphql`
  query adminDashboard_ActiveUsersQuery {
    adminActiveUsers {
      id
      email
      activatedAt
      accessGrantedAt
      lastActivityAt
      dataRevealedAt
      publicSlug
      filesCount
      rowsTotal
      transactionsCount
      categoriesCount
      stakeholdersCount
      estimatedMrr
    }
  }
`;

const grantMutation = graphql`
  mutation adminDashboard_GrantMutation($id: ID!) {
    adminUserGrantAccess(userId: $id) {
      activeUser {
        id
      }
      errors {
        message
      }
    }
  }
`;

const impersonateMutation = graphql`
  mutation adminDashboard_ImpersonateMutation($id: ID!) {
    adminUserImpersonate(userId: $id) {
      token
      errors {
        message
      }
    }
  }
`;

const slugSetMutation = graphql`
  mutation adminDashboard_SlugSetMutation($id: ID!, $slug: String!) {
    adminUserSlugSet(userId: $id, slug: $slug) {
      activeUser {
        id
        publicSlug
      }
      errors {
        message
      }
    }
  }
`;

type Waitlist = NonNullable<
  adminDashboard_WaitlistQuery["response"]["adminWaitlist"]
>;
type ActiveUsers = NonNullable<
  adminDashboard_ActiveUsersQuery["response"]["adminActiveUsers"]
>;

const usd = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(n);
const kb = (bytes: number | null | undefined) =>
  bytes == null ? "—" : `${Math.max(1, Math.round(bytes / 1024))} KB`;
const date = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : "—");
// The line the team's new-signup mail gives (SignupMailer#source_line): the
// cxcash.com page and button that sent them, then where their visit to the
// site began and what sent them to it. A link from elsewhere has no page, so
// its campaign tags stand in.
const source = (attribution: Waitlist[number]["attribution"]) => {
  if (!attribution) return "unknown";
  const { page, utmSource, utmMedium, utmCampaign, utmContent, landing, ref } =
    attribution;
  const place =
    page ??
    ([utmSource, utmMedium, utmCampaign].filter(Boolean).join("/") ||
      attribution.referrer) ??
    "unknown";
  return [
    place,
    utmContent && ` (${utmContent})`,
    landing && `, entered on ${landing}`,
    ref && ` via ${ref}`,
  ]
    .filter(Boolean)
    .join("");
};

export default function AdminDashboard({ onLogout }: { onLogout: () => void }) {
  const [tab, setTab] = useState<"waitlist" | "active">("waitlist");
  const [waitlist, setWaitlist] = useState<Waitlist>([]);
  const [active, setActive] = useState<ActiveUsers>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(() => {
    fetchQuery<adminDashboard_WaitlistQuery>(environment, waitlistQuery, {})
      .toPromise()
      .then((d) => setWaitlist((d?.adminWaitlist ?? []) as Waitlist));
    fetchQuery<adminDashboard_ActiveUsersQuery>(
      environment,
      activeUsersQuery,
      {}
    )
      .toPromise()
      .then((d) => setActive((d?.adminActiveUsers ?? []) as ActiveUsers));
  }, []);

  useEffect(reload, [reload]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const grant = (id: string) => {
    setBusyId(id);
    commitMutation<adminDashboard_GrantMutation>(environment, {
      mutation: grantMutation,
      variables: { id },
      onCompleted: () => {
        setBusyId(null);
        reload();
      },
      onError: () => setBusyId(null),
    });
  };

  const impersonate = (id: string) => {
    commitMutation<adminDashboard_ImpersonateMutation>(environment, {
      mutation: impersonateMutation,
      variables: { id },
      onCompleted: (res) => {
        const token = res.adminUserImpersonate?.token;
        if (token) {
          startImpersonation(token);
          window.location.assign("/");
        }
      },
    });
  };

  const editSlug = (id: string, current: string | null | undefined) => {
    const slug = window.prompt("Public slug for this account:", current ?? "");
    if (!slug) return;
    commitMutation<adminDashboard_SlugSetMutation>(environment, {
      mutation: slugSetMutation,
      variables: { id, slug },
      onCompleted: (res) => {
        const err = res.adminUserSlugSet?.errors?.[0]?.message;
        if (err) window.alert(err);
        reload();
      },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3">
        <div className="flex items-center gap-1">
          <TabButton
            active={tab === "waitlist"}
            onClick={() => setTab("waitlist")}
          >
            Waiting list ({waitlist.length})
          </TabButton>
          <TabButton active={tab === "active"} onClick={() => setTab("active")}>
            Active users ({active.length})
          </TabButton>
        </div>
        <button
          onClick={onLogout}
          className="text-sm text-gray-500 hover:text-gray-900"
        >
          Log out
        </button>
      </header>

      <main className="mx-auto max-w-6xl p-6">
        {tab === "waitlist" ? (
          <section>
            <h1 className="mb-4 text-xl font-bold text-gray-900">
              Waiting list
            </h1>
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              {waitlist.length === 0 && (
                <p className="p-6 text-sm text-gray-400">
                  No one on the waiting list yet.
                </p>
              )}
              {/* In rank order, so the row's place IS its queue position. */}
              {waitlist.map((entry, rank) => (
                <div
                  key={entry.id}
                  className="border-b border-gray-100 last:border-0"
                >
                  <div className="flex items-center justify-between gap-4 px-4 py-3">
                    <button
                      onClick={() => toggle(entry.id)}
                      className="flex shrink-0 items-center gap-2 text-left"
                    >
                      <span className="text-gray-400">
                        {expanded.has(entry.id) ? "▾" : "▸"}
                      </span>
                      <span className="font-medium text-gray-900">
                        {entry.name ?? entry.email}
                      </span>
                      <span className="text-sm text-gray-500">
                        {entry.email}
                      </span>
                      {/* The fit score is why this row is where it is. It is
                          admin-only on purpose — it says which leads we would
                          rather have, which is ours to act on and not theirs to
                          read. */}
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium tabular-nums text-gray-700">
                        fit {entry.fitScore}
                        {entry.referralBonus > 0 && ` +${entry.referralBonus}`}
                      </span>
                      <span className="text-xs text-gray-500">#{rank + 1}</span>
                    </button>
                    {/* Where they came from is the one thing here that gives
                        way when the row runs short: it truncates, with the
                        whole line on hover, and nothing else wraps. */}
                    <div className="flex min-w-0 items-center gap-4 whitespace-nowrap text-xs text-gray-500">
                      <span
                        className="min-w-0 max-w-xs truncate"
                        title={source(entry.attribution)}
                      >
                        from {source(entry.attribution)}
                      </span>
                      <span>code {entry.referralCode ?? "—"}</span>
                      {entry.referredByEmail && (
                        <span>via {entry.referredByEmail}</span>
                      )}
                      {/* Review the prepped (hidden) account by impersonating it — the
                          server gate is bypassed for staff, so you see the ingested data
                          before the user ever reveals it. */}
                      <button
                        onClick={() => impersonate(entry.id)}
                        disabled={busyId === entry.id}
                        className="rounded border border-gray-300 px-3 py-1.5 font-medium text-gray-700 disabled:opacity-50"
                      >
                        Review (impersonate)
                      </button>
                      {/* Approve = grant access + ingest the account's onboarding
                          statements into its scoped pipeline (prepared invisibly; the
                          user sees it appear on their "Process my files" click). */}
                      <button
                        onClick={() => grant(entry.id)}
                        disabled={busyId === entry.id}
                        className="rounded bg-green-600 px-3 py-1.5 font-medium text-white disabled:opacity-50"
                        title="Grants access and ingests this account's statements (hidden until the user reveals)"
                      >
                        {busyId === entry.id
                          ? "Approving…"
                          : "Approve & ingest"}
                      </button>
                    </div>
                  </div>
                  {expanded.has(entry.id) && (
                    <div className="space-y-3 bg-gray-50 px-8 py-3">
                      {/* Why they rank where they do, dimension by dimension.
                          This is the panel to read when tuning the weights in
                          Onboarding::Scoring — it shows what the current ones
                          actually said about a real lead. */}
                      <div className="rounded border border-gray-200 bg-white p-3">
                        <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                          <span className="font-medium text-gray-800">
                            {entry.status}
                          </span>
                          <span>data {entry.completenessScore}%</span>
                          <span>{entry.files.length} files</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {entry.scoreBreakdown.map((d) => (
                            <span
                              key={d.name}
                              className="rounded bg-gray-50 px-2 py-1 text-xs text-gray-600"
                            >
                              {d.name}{" "}
                              <span className="font-medium tabular-nums text-gray-900">
                                {d.points}
                              </span>
                              <span className="text-gray-400">/{d.max}</span>
                            </span>
                          ))}
                        </div>
                        {entry.files.length > 0 && (
                          <table className="mt-3 w-full text-xs">
                            <thead className="text-gray-400">
                              <tr className="text-left">
                                <th className="py-1 font-normal">File</th>
                                <th className="py-1 font-normal">Size</th>
                                <th className="py-1 font-normal">Rows</th>
                                <th className="py-1 font-normal">Income</th>
                                <th className="py-1 font-normal">State</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entry.files.map((f) => (
                                <tr
                                  key={f.id}
                                  className="border-t border-gray-50 text-gray-600"
                                >
                                  <td className="py-1">{f.filename ?? "—"}</td>
                                  <td className="py-1">{kb(f.byteSize)}</td>
                                  <td className="py-1">{f.txnCount}</td>
                                  <td className="py-1">
                                    {usd(f.detectedIncome)}
                                  </td>
                                  <td className="py-1">{f.parseState}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ) : (
          <section>
            <h1 className="mb-4 text-xl font-bold text-gray-900">
              Active users
            </h1>
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
                  <tr>
                    {[
                      "Email",
                      "Activated",
                      "Last activity",
                      "Files",
                      "Txns",
                      "Cats",
                      "Stakeholders",
                      "MRR",
                      "",
                    ].map((h) => (
                      <th key={h} className="px-3 py-2 font-medium">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {active.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-3 py-6 text-center text-gray-400"
                      >
                        No active users yet.
                      </td>
                    </tr>
                  )}
                  {active.map((u) => (
                    <tr
                      key={u.id}
                      className="border-t border-gray-100 text-gray-700"
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {u.email}
                        {u.publicSlug && (
                          <a
                            href={`/${u.publicSlug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-2 text-xs text-blue-600"
                          >
                            /{u.publicSlug}
                          </a>
                        )}
                      </td>
                      <td className="px-3 py-2">{date(u.activatedAt)}</td>
                      <td className="px-3 py-2">{date(u.lastActivityAt)}</td>
                      <td className="px-3 py-2">
                        {u.filesCount} ({u.rowsTotal} rows)
                      </td>
                      <td className="px-3 py-2">{u.transactionsCount}</td>
                      <td className="px-3 py-2">{u.categoriesCount}</td>
                      <td className="px-3 py-2">{u.stakeholdersCount}</td>
                      <td className="px-3 py-2">{usd(u.estimatedMrr)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => editSlug(u.id, u.publicSlug)}
                          className="mr-2 text-xs text-gray-500 hover:text-gray-900"
                        >
                          slug
                        </button>
                        <button
                          onClick={() => impersonate(u.id)}
                          className="rounded bg-gray-900 px-2.5 py-1 text-xs font-medium text-white"
                        >
                          Impersonate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "rounded px-3 py-1.5 text-sm font-medium",
        active ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
      )}
    >
      {children}
    </button>
  );
}
